import { useState } from "react"
import { ChevronDown, ChevronUp, Type, FileText, Plus, Trash2, Tag } from "lucide-react"

const AdsTextConfigurationSection = ({ data, platform, editableData, editMode, onChange, loading }) => {
  const adNamesMap = (data?.ad_content || []).reduce((map, ad) => {
    map[ad.id] = ad.name
    return map
  }, {})

  const groupNamesMap = (data?.ad_groups || []).reduce((map, group) => {
    map[group.id] = group.name
    return map
  }, {})

  const sectionToDisplayName = {
    headlines: "Títulos",
    descriptions: "Descripciones",
    keywords: "Palabras clave",
  }

  const limitsByPlatformAndType = {
    google: {
      default: {
        headlines: { min: 1, max: 15 },
        descriptions: { min: 1, max: 4 },
      },
      PERFORMANCE_MAX: {
        headlines: { min: 5, max: 15 },
        descriptions: { min: 2, max: 5 },
      },
      DISPLAY: {
        headlines: { min: 1, max: 5 },
        descriptions: { min: 1, max: 5 },
      },
      SEARCH: {
        headlines: { min: 3, max: 15 },
        descriptions: { min: 2, max: 4 },
        keywords: { min: 1, max: 50 },
      },
    },
    meta: {
      default: {
        headlines: { min: 1, max: 1 },
        descriptions: { min: 1, max: 1 },
      },
    },
  }

  const campaignType = data?.campaign_type || 'default'
  const platformLimits = limitsByPlatformAndType[platform] || {}
  const limits = platformLimits[campaignType] || platformLimits.default || { headlines: { min: 1, max: 1 }, descriptions: { min: 1, max: 1 } }

  const [expandedSections, setExpandedSections] = useState({
    headlines: false,
    descriptions: false,
    keywords: false,
  })

  const [expandedAds, setExpandedAds] = useState(() => {
    const initialState = {}
    if (editableData?.headlines) {
      const firstHeadlineAdId = Object.keys(editableData.headlines)[0]
      if (firstHeadlineAdId) {
        initialState[`headlines_${firstHeadlineAdId}`] = true
      }
    }
    if (editableData?.descriptions) {
      const firstDescriptionAdId = Object.keys(editableData.descriptions)[0]
      if (firstDescriptionAdId) {
        initialState[`descriptions_${firstDescriptionAdId}`] = true
      }
    }

    if (editableData?.keywords && campaignType === 'SEARCH') {
      const firstGroupId = data?.ad_groups?.[0]?.id
      if (firstGroupId) initialState[`keywords_${firstGroupId}`] = true
    }
    return initialState
  })

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const toggleAd = (adId, section) => {
    const key = `${section}_${adId}`
    setExpandedAds((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const addItem = (adId, fieldName, section) => {
    const currentItems = editableData[fieldName][adId] || []
    const limit = limits[section]

    if (currentItems.length < limit.max) {
      const updatedItems = [...currentItems, ""]
      const newMap = { ...editableData[fieldName], [adId]: updatedItems }
      onChange(fieldName, newMap)
    }
  }

  const removeItem = (adId, fieldName, section, itemIndex) => {
    const currentItems = editableData[fieldName][adId] || []
    const limit = limits[section]

    if (currentItems.length > limit.min) {
      const updatedItems = currentItems.filter((_, idx) => idx !== itemIndex)
      const newMap = { ...editableData[fieldName], [adId]: updatedItems }
      onChange(fieldName, newMap)
    }
  }

  const renderTextOrList = (items, fallback = "Sin definir") => {
    if (!items || items.length === 0) return <span className="text-white/50">{fallback}</span>
    if (items.length === 1) return <span className="text-white">{items[0]}</span>
    return (
      <ul className="list-disc pl-5 space-y-1.5">
        {items.map((item, idx) => (
          <li key={idx} className="text-sm text-white">
            {item}
          </li>
        ))}
      </ul>
    )
  }

  const renderReadModeSection = (title, icon, items, section) => {
    const flatItems = Object.values(items || {}).flat()
    const notDuplicatedItems = [...new Set(flatItems.map((item) => String(item).trim()).filter(Boolean))]
    const isExpanded = expandedSections[section]
    const shouldTruncate = notDuplicatedItems.length > 4
    const displayItems = shouldTruncate && !isExpanded ? notDuplicatedItems.slice(0, 4) : notDuplicatedItems

    return (
      <div className={`bg-white/10 rounded-xl border border-white/20 p-4 ${section === "keywords" ? "sm:col-span-4" : "sm:col-span-2"}`}>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-white/70">{icon}</span>
          <span className="text-xs text-white/70">{title}</span>
        </div>
        <div className="text-sm break-words">
          {renderTextOrList(displayItems)}
          {shouldTruncate && (
            <div className="flex justify-center mt-3">
              <button
                onClick={() => toggleSection(section)}
                className="text-xs text-white/70 hover:text-white bg-white/10 hover:bg-white/20 px-3 py-1 rounded-full transition-colors"
              >
                {isExpanded ? "Ver menos" : "Ver más"}
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderEditModeSection = (title, icon, items, fieldName, section, charLimit) => {
    const limit = limits[section]
    if (platform === "meta") charLimit = 200

    return (
      <div className={`bg-white/10 rounded-xl border border-white/20 p-4 ${section === "keywords" ? "sm:col-span-4" : "sm:col-span-2"}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-white/70">{icon}</span>
            <span className="text-xs text-white/70">{title}</span>
          </div>
        </div>
        <div className="space-y-4">
          {Object.entries(items || {}).map(([adId, values], adIndex) => {
            const expandedKey = `${section}_${adId}`
            const isAdExpanded = expandedAds[expandedKey] || false
            const currentCount = (values || []).filter((value) => String(value).trim() !== "").length
            const canAdd = currentCount < limit.max
            const canRemove = currentCount > limit.min

            return (
              <div key={adId} className={`space-y-2 ${adIndex > 0 ? "pt-4 border-t border-white/20" : ""}`}>
                <div
                  className="flex items-center justify-between cursor-pointer hover:bg-white/10 -mx-2 px-2 py-1 rounded"
                  onClick={() => toggleAd(adId, section)}
                >
                  <div className="flex items-center gap-2">
                    <div className="text-xs font-medium text-white/70">
                      {section === "keywords"
                        ? (groupNamesMap[adId] || `Grupo ${adIndex + 1}`)
                        : (adNamesMap[adId]   || `Anuncio ${adIndex + 1}`)
                      }
                    </div>

                    <div className="text-xs text-white/50">
                      ({currentCount}/{limit.max})
                    </div>
                  </div>
                  <div className="text-white/70 transition-colors">
                    {isAdExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>
                {isAdExpanded && (
                  <div className={section === "keywords"
                    ? "grid grid-cols-1 sm:grid-cols-2 gap-2"
                    : "space-y-2"
                  }>
                    {values.length > 0 ? (
                      values.map((value, idx) => (
                        <div key={idx} className="relative flex gap-2">
                          {section === "descriptions" ? (
                            <textarea
                              value={value}
                              maxLength={charLimit}
                              onChange={(e) => {
                                const updated = [...values]
                                updated[idx] = e.target.value.slice(0, charLimit)
                                const newMap = { ...items, [adId]: updated }
                                onChange(fieldName, newMap)
                              }}
                              style={{ 
                                fieldSizing: 'content',
                                resize: 'none',
                                minHeight: '2.5rem',
                                maxHeight: '4.5rem',
                                overflowY: 'auto'
                              }}
                              className={`flex-1 border border-white/20 rounded-xl pl-4 pr-16 py-2.5 text-sm text-white bg-white/10 focus:outline-none focus:ring-2 focus:ring-fuchsia-400/20 focus:border-fuchsia-400 transition-all`}
                              placeholder="---"
                              rows={1}
                            />
                          ) : (
                            <input
                              type="text"
                              value={value}
                              maxLength={platform === "meta" ? undefined : charLimit}
                              onChange={(e) => {
                                const updated = [...values]
                                updated[idx] = e.target.value.slice(0, charLimit)
                                const newMap = { ...items, [adId]: updated }
                                onChange(fieldName, newMap)
                              }}
                              className={`flex-1 border border-white/20 rounded-xl pl-4 pr-16 py-2.5 text-sm text-white bg-white/10 focus:outline-none focus:ring-2 focus:ring-fuchsia-400/20 focus:border-fuchsia-400 transition-all`}
                              placeholder="---"
                            />
                          )}
                          {platform !== "meta" && (
                            <span className="absolute top-3 text-xs text-white/50 bg-slate-800 px-1" style={{ right: "3.75rem" }}>
                              {value.length}/{charLimit}
                            </span>
                          )}
                          {platform !== "meta" && (
                            <button
                              onClick={() => canRemove && removeItem(adId, fieldName, section, idx)}
                              disabled={!canRemove}
                              className={`flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                                canRemove
                                  ? "text-red-400 hover:text-red-300 hover:bg-red-500/20 cursor-pointer"
                                  : "text-white/30 cursor-not-allowed disabled"
                              }`}
                              title={
                                canRemove ? "Eliminar elemento" : `Mínimo ${limits[section].min} elementos requerido`
                              }
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-white/50 italic">Anuncio vacío</div>
                    )}

                    {platform !== "meta" && canAdd && (
                      <button
                        onClick={() => addItem(adId, fieldName, section)}
                        className="w-full border-2 border-dashed border-white/20 hover:border-white/30 rounded-xl py-3 flex items-center justify-center gap-2 text-white/70 hover:text-white transition-colors"
                      >
                        <Plus size={16} />
                        <span className="text-sm">Agregar {sectionToDisplayName[section]}</span>
                      </button>
                    )}

                    {platform !== "meta" && (
                      <div className="text-xs text-white/50 text-center">
                        {!canAdd && `Máximo ${limit.max} elementos alcanzado`}
                        {!canRemove && currentCount <= limit.min && `Mínimo ${limit.min} elementos requerido`}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className={`font-sans mt-6${loading ? " disabled" : ""}`}>
      <h3 className="text-sm font-medium text-white/70 mb-3">Configuración de anuncios</h3>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {editMode ? (
          <>
            {renderEditModeSection("Títulos", <Type size={16} />, editableData.headlines, "headlines", "headlines", 30)}
            {renderEditModeSection(
              "Descripciones",
              <FileText size={16} />,
              editableData.descriptions,
              "descriptions",
              "descriptions",
              90,
            )}
            { campaignType === "SEARCH" &&
              renderEditModeSection(
                "Palabras clave",
                <Tag size={16} />,
                editableData.keywords,
                "keywords",
                "keywords",
                25
              )
            }
          </>
        ) : (
          <>
            {renderReadModeSection("Títulos", <Type size={16} />, editableData.headlines, "headlines")}
            {renderReadModeSection("Descripciones", <FileText size={16} />, editableData.descriptions, "descriptions")}
            { campaignType === "SEARCH" && renderReadModeSection("Palabras clave", <Tag size={16} />, editableData.keywords, "keywords")}

          </>
        )}
      </div>
    </div>
  )
}

export default AdsTextConfigurationSection
